/**
 * UImg — image éditable du runtime O1X.
 *
 * Tag type `uimg` (registre `HtmlService:182`). Trois modes :
 *  - **display** : src statique, pas de CTA — le mode par défaut.
 *  - **upload direct** : `editable`/`uploadable`/`formid` → input file natif (UImgUploadMode).
 *  - **sélecteur modal** : `sd` (selection data) → DSelector / EditorMultiMedia (UImgSelectorMode).
 *
 * UImg orchestre — la logique métier est dans 3 collaborateurs :
 *  - `UImgSourceResolver` : résolution path/src/mid.
 *  - `UImgUploadMode`     : input file + POST direct.
 *  - `UImgSelectorMode`   : modal FSApp + callback itemClick.
 *
 * ⚠️ Ne PAS faire de UImg un form-item — il s'auto-soumet via uploadFile/itemClicked
 * (cf. ha/447 brief, faux positif audit).
 */

import { toMap } from "../../../../HA";
import { Container } from "../Container/Container";
import { FlexContainer } from "../Container/FlexContainer";
import { CFlexContainer } from "../Container/CFlexContainer";
import { Div } from "../Div";
import { Tag } from "../Tag";
import { Img } from "../Img";
import { UImgSourceResolver } from "./UImgSourceResolver";
import { UImgUploadMode } from "./UImgUploadMode";
import { UImgSelectorMode } from "./UImgSelectorMode";

export class UImg extends Container {

    private _resolver?: UImgSourceResolver;
    private _upload?: UImgUploadMode;
    private _selector?: UImgSelectorMode;

    protected getImg(): Img {
        return this.get("img");
    }

    public getImageSrc(): string {
        return this.getResolver().getImageSrc();
    }

    public isStaticSource(): boolean {
        return this.getResolver().isStaticSource();
    }

    public setResolvedSource(source: string): void {
        this.addToData("src", source);
        this.getImg().updateSrc(source);
    }

    public getUploadFieldName(): string {
        return this.getUpload().getUploadFieldName();
    }

    protected createSelf(): void {
        super.createSelf();
        this.addClass("uimg");
        const cont = this.get("fc") ? new FlexContainer() : new CFlexContainer();
        const img = new Img(toMap({
            alt: this.get("ti"),
            h: this.get("h"),
            load: this.get("loading"),
            src: this.getImageSrc(),
            w: this.get("w"),
        }));
        this.addToData("img", img);
        this.appendChild(cont);
        const imgC = new Div({ class: "imgc" });
        this.addToData("imgc", imgC);
        imgC.appendChild(img);
        cont.appendChild(imgC);
        if (this.has("ti")) {
            const ti = new Div(this.translate(this.get("ti")));
            ti.addClass("ti");
            cont.appendChild(ti);
        }

        if (this.isConfigurable()) {
            this.addClass("uimg--editable");
            imgC.addClass("editable");
            const ctac = new Div({ classes: ["ctac", "btnShadow"] });
            const cta = new Tag({ html: "<i></i>", classes: ["rimg", "las", "la-camera"] });
            this.addToData("cta", ctac);
            ctac.appendChild(cta);
            cont.appendChild(ctac);
            cont.onClick((event: MouseEvent) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.handleClick();
            });
            if (this.isDirectUpload()) {
                this.getUpload().ensureFileInput();
            }
        }
    }

    private handleClick(): void {
        if (this.isDirectUpload()) {
            this.getUpload().openFilePicker();
            return;
        }
        this.getSelector().openModal();
    }

    private isConfigurable(): boolean {
        const sd = this.get("sd");
        return !!(sd && sd !== "") || this.isDirectUpload();
    }

    private isDirectUpload(): boolean {
        return !!(this.get("editable") || this.get("uploadable") || this.get("formid"))
            && !(this.get("sd"));
    }

    // ─── Collaborateurs (lazy, host = this) ───────────────────────────────────

    private getResolver(): UImgSourceResolver {
        if (!this._resolver) {
            this._resolver = new UImgSourceResolver(this as any);
        }
        return this._resolver;
    }

    private getUpload(): UImgUploadMode {
        if (!this._upload) {
            this._upload = new UImgUploadMode(this as any);
        }
        return this._upload;
    }

    private getSelector(): UImgSelectorMode {
        if (!this._selector) {
            this._selector = new UImgSelectorMode(this as any);
        }
        return this._selector;
    }
}
